// In-place record translation (ported from the mock-up): collect the visible text nodes of the
// active tab, translate them in one backend call, swap them in place, and restore on demand.
import { useCallback, useEffect, useRef, useState, type RefObject } from "react";
import { api } from "../../api";

const LANGS: [string, string][] = [
  ["Polish", "🇵🇱 Polish"], ["Spanish", "🇪🇸 Spanish"], ["French", "🇫🇷 French"], ["German", "🇩🇪 German"],
  ["Italian", "🇮🇹 Italian"], ["Portuguese", "🇵🇹 Portuguese"], ["Dutch", "🇳🇱 Dutch"], ["Ukrainian", "🇺🇦 Ukrainian"],
  ["Russian", "🇷🇺 Russian"], ["Mandarin Chinese", "🇨🇳 Mandarin"], ["Japanese", "🇯🇵 Japanese"], ["Korean", "🇰🇷 Korean"], ["Arabic", "🇸🇦 Arabic"],
];

// Labels that should never be translated (values, codes, flags, controls).
const SKIP = "canvas,select,option,button,svg,script,style,textarea,input,[data-notranslate],.cb,.fl,.lv,.lrf,.pid,.vo,.atag-red,.legend,.hash,.chain-hash";

function collect(root: HTMLElement): Text[] {
  const skip = new Set<Element>();
  root.querySelectorAll(SKIP).forEach((el) => skip.add(el));
  const out: Text[] = [];
  const walk = (n: Node) => {
    if (n.nodeType === Node.TEXT_NODE) {
      const t = n.textContent?.trim() ?? "";
      if (t.length < 3 || /^[\d.\-/+\s%µKgdLpmoflU:,·—–]+$/.test(t)) return;
      out.push(n as Text);
    } else if (n.nodeType === Node.ELEMENT_NODE && !skip.has(n as Element)) {
      n.childNodes.forEach(walk);
    }
  };
  walk(root);
  return out;
}

export interface TranslationState {
  lang: string | null;
  status: string;
  start: (lang: string) => void;
  clear: () => void;
}

export function useRecordTranslation(ref: RefObject<HTMLElement | null>, tab: string): TranslationState {
  const [lang, setLang] = useState<string | null>(null);
  const [status, setStatus] = useState("");
  const originals = useRef(new Map<Text, string>());
  const cache = useRef(new Map<string, string>()); // `${lang}\u0000${original}` → translated

  const restore = useCallback(() => {
    originals.current.forEach((text, node) => { if (node.isConnected) node.textContent = text; });
    originals.current.clear();
  }, []);

  const translateVisible = useCallback(async (target: string) => {
    const root = ref.current;
    if (!root) return;
    const nodes = collect(root).filter((n) => !originals.current.has(n));
    if (!nodes.length) return;
    nodes.forEach((n) => originals.current.set(n, n.textContent!));
    const texts = nodes.map((n) => originals.current.get(n)!.trim());
    const missing = [...new Set(texts.filter((t) => !cache.current.has(`${target}\u0000${t}`)))];
    try {
      if (missing.length) {
        setStatus("Translating…");
        const { translations } = await api.translate(target, missing);
        missing.forEach((t, i) => cache.current.set(`${target}\u0000${t}`, translations[i]));
      }
      nodes.forEach((n, i) => {
        const tr = cache.current.get(`${target}\u0000${texts[i]}`);
        if (tr && n.isConnected) n.textContent = n.textContent!.replace(texts[i], tr);
      });
      setStatus(`🌐 ${target}`);
    } catch (e) {
      nodes.forEach((n) => originals.current.delete(n));
      setStatus(`⚠ ${(e as Error).message}`);
    }
  }, [ref]);

  // Re-apply after every tab switch (new DOM nodes).
  useEffect(() => {
    if (!lang) return;
    const id = requestAnimationFrame(() => translateVisible(lang));
    return () => cancelAnimationFrame(id);
  }, [lang, tab, translateVisible]);

  return {
    lang,
    status,
    start: (l) => { restore(); setLang(l); },
    clear: () => { restore(); setLang(null); setStatus(""); },
  };
}

export function TranslateBar({ t }: { t: TranslationState }) {
  const [choice, setChoice] = useState("");
  return (
    <div className="tbar">
      <span className="lbl">🌐 Translate record</span>
      {!t.lang ? (
        <>
          <select value={choice} onChange={(e) => setChoice(e.target.value)} aria-label="Language">
            <option value="">Select language…</option>
            {LANGS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <button className="btn sm" disabled={!choice} onClick={() => t.start(choice)}>Translate</button>
        </>
      ) : (
        <>
          <span className="cb bt">🌐 {t.lang}</span>
          <span style={{ fontSize: 11, color: "var(--txt3)", fontStyle: "italic" }}>{t.status}</span>
          <button className="btn ghost sm" style={{ marginLeft: "auto" }} onClick={() => { t.clear(); setChoice(""); }}>✕ View original</button>
        </>
      )}
    </div>
  );
}
